import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './Sobre.module.css';
import imgOffice from './img/office.png';

const AnimatedCounter = ({ end, prefix = '', suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 } 
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  }, [isVisible, end, duration]);

  return (
    <span ref={ref}>
      {prefix}{count}{suffix}
    </span>
  );
};


function Sobre() {
  return (
    <div className={styles.paginaSobre}>
      
      <section className={styles.bannerSobre}></section>

      <section className={`${styles.secaoGenerica} ${styles.bgBranco} ${styles.secaoSobreGeral}`}>
        
        <div className={styles.cabecalhoPagina}>
          <h1>Sobre a CPP Contabilidade</h1>
          <p className={styles.subtituloAzul}>Conheça um pouco de nossa história.</p>
        </div>

        <div className={styles.containerGrid}>
          <div className={styles.textos}>
            <h2 className={styles.tituloSecao}>Nossa Trajetória</h2>
            <h3 className={styles.subtituloAzul}>Inteligência contábil além das obrigações</h3>
            
            <p>A <strong>CPP Contabilidade</strong> foi concebida com um propósito inegociável: transcender o modelo reativo e burocrático da contabilidade tradicional. Desde a nossa fundação, a premissa foi clara: ir muito além da mera entrega de obrigações fiscais, provendo organização estrutural, segurança e um acompanhamento consultivo real para empresas que demandam controle e previsibilidade operacional.</p>
            
            <p>Aliando sólida experiência técnica à vivência direta no cotidiano corporativo de nossos parceiros, desenvolvemos metodologias que garantem transparência absoluta nos dados financeiros. Nossa atuação mitiga passivos, elimina riscos desnecessários e permite que a diretoria exerça uma gestão altamente eficiente.</p>
            
            <p>Ao longo de nossa trajetória, consolidamos uma carteira formada por corporações que valorizam a responsabilidade, a organização e a tomada de decisões bem fundamentadas. Mais do que serviços contábeis, <strong>entregamos tranquilidade, controle e confiança</strong> para o crescimento sustentável do seu negócio.</p>
          </div>

          <div className={styles.imagemWrapper}>
            <img src={imgOffice} alt="Equipe CPP Contabilidade" className={styles.imagemEspaco}/>
          </div>
        </div>
        <div className={styles.mvvContainerFull}>
          <div className={styles.mvvItem}>
            <h4>Nossa Missão</h4>
            <p>Garantir organização, segurança e conformidade para as empresas, por meio de uma atuação contábil responsável, estruturada e rigorosamente orientada à tomada de decisão.</p>
          </div>

          <div className={styles.mvvItem}>
            <h4>Nossa Visão</h4>
            <p>Ser a principal referência para corporações que buscam mais do que um escritório contábil: um parceiro estratégico capaz de trazer clareza, controle e segurança para o crescimento escalável do negócio.</p>
          </div>

          <div className={styles.mvvItem}>
            <h4>Nossos Valores</h4>
            <ul className={styles.listaValores}>
              <li>Responsabilidade técnica e ética</li>
              <li>Clareza e organização</li>
              <li>Compromisso com prazos</li>
              <li>Transparência irrestrita</li>
              <li>Foco em resultados</li>
              <li>Relacionamento direto</li>
            </ul>
          </div>
        </div>
      </section>

      <section className={`${styles.secaoDados} ${styles.bgClaro}`}>
        <div className={styles.conteudoSecao}>
          
          <div className={styles.cardsGrid}>
            
            <div className={`${styles.infoCard} ${styles.cardBranco}`}>
              <h3>Serviços<br/>Contábeis</h3>
              <ul>
                <li>Contabilidade Geral</li>
                <li>Escrituração Fiscal</li>
                <li>Análise Financeira</li>
              </ul>
            </div>

            <div className={`${styles.infoCard} ${styles.cardCyan}`}>
              <h3>Serviços<br/>Fiscais</h3>
              <ul>
                <li>Planejamento Tributário</li>
                <li>Regularização Fiscal</li>
                <li>Consultoria Fiscal</li>
              </ul>
            </div>

            <div className={`${styles.infoCard} ${styles.cardBranco}`}>
              <h3>Serviços<br/>Trabalhistas</h3>
              <ul>
                <li>Folha de Pagamento</li>
                <li>Gestão de Benefícios</li>
                <li>Consultoria Trabalhista</li>
              </ul>
            </div>

            <div className={`${styles.infoCard} ${styles.cardCyan}`}>
              <h3>Atendimento<br/>especializado</h3>
              <ul>
                <li>Empresas industriais</li>
                <li>Empresas comerciais</li>
                <li>Prestadoras de serviços</li>
              </ul>
            </div>

          </div>

          <div className={styles.statsContainer}>
            
            <div className={styles.statItem}>
              <span className={styles.statNumero}>
                <AnimatedCounter end={10} prefix="+" />
              </span>
              <span className={styles.statTexto}>Anos de experiência</span>
            </div>

            <div className={styles.divisorVertical}></div>

            <div className={styles.statItem}>
              <span className={styles.statNumero}>
                {/* Aqui ele vai de 0 a 4 e adiciona o 'mil' no final */}
                <AnimatedCounter end={4} prefix="+" suffix="mil" /> 
              </span>
              <span className={styles.statTexto}>Clientes atendidos</span>
            </div>

            <div className={styles.statCard}>
              <span className={styles.statNumeroCard}>
                <AnimatedCounter end={50} prefix="+" />
              </span>
              <span className={styles.statTextoCard}>Empresas</span>
            </div>

          </div>

        </div>
      </section>

      <section className={`${styles.consideracoesSecao} ${styles.bgAzul}`}>
        <div className={styles.conteudoSecao}>
          <h2 className={styles.textoBranco}>Pronto para transformar sua gestão?</h2>
          <p className={styles.textoBranco}>Nossa equipe está preparada para oferecer as melhores soluções para o seu negócio crescer com segurança. Gostaria de mais informações?</p>
          <Link to="/contato" className={`${styles.btnLink} ${styles.btnCyan}`}>Entre em contato</Link>
        </div>
      </section>

    </div>
  );
}

export default Sobre;